<template>
  <div class="analysis-page">
    <div class="page-header">
      <h1>用户活跃度分析</h1>
      <button @click="goBack" class="back-btn">返回</button>
    </div>

    <!-- 统计数据卡片 -->
    <div class="stats-container">
      <div class="stat-card primary">
        <div class="stat-title">总用户数</div>
        <div class="stat-value">{{ formatNumber(statsData.totalUsers) }}</div>
        <div class="stat-change" :class="getChangeClass(statsData.totalUsersChangePercent)">
          <span v-if="statsData.totalUsersChangePercent > 0">↑</span>
          <span v-else-if="statsData.totalUsersChangePercent < 0">↓</span>
          <span v-else>→</span>
          {{ Math.abs(statsData.totalUsersChangePercent) }}%
        </div>
      </div>

      <div class="stat-card success">
        <div class="stat-title">日活跃用户 (DAU)</div>
        <div class="stat-value">{{ formatNumber(statsData.dau) }}</div>
        <div class="stat-change" :class="getChangeClass(statsData.dauChangePercent)">
          <span v-if="statsData.dauChangePercent > 0">↑</span>
          <span v-else-if="statsData.dauChangePercent < 0">↓</span>
          <span v-else>→</span>
          {{ Math.abs(statsData.dauChangePercent) }}%
        </div>
      </div>

      <div class="stat-card warning">
        <div class="stat-title">周活跃用户 (WAU)</div>
        <div class="stat-value">{{ formatNumber(statsData.wau) }}</div>
        <div class="stat-change" :class="getChangeClass(statsData.wauChangePercent)">
          <span v-if="statsData.wauChangePercent > 0">↑</span>
          <span v-else-if="statsData.wauChangePercent < 0">↓</span>
          <span v-else>→</span>
          {{ Math.abs(statsData.wauChangePercent) }}%
        </div>
      </div>

      <div class="stat-card danger">
        <div class="stat-title">月活跃用户 (MAU)</div>
        <div class="stat-value">{{ formatNumber(statsData.mau) }}</div>
        <div class="stat-change" :class="getChangeClass(statsData.mauChangePercent)">
          <span v-if="statsData.mauChangePercent > 0">↑</span>
          <span v-else-if="statsData.mauChangePercent < 0">↓</span>
          <span v-else>→</span>
          {{ Math.abs(statsData.mauChangePercent) }}%
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-container">
      <div class="chart-card">
        <div class="chart-header">
          <h2>活跃度趋势</h2>
          <div class="date-buttons">
            <el-button
              size="small"
              :type="trendParams.type === 'day' ? 'primary' : ''"
              @click="changeTrendType('day', 30)"
            >
              日
            </el-button>
            <el-button
              size="small"
              :type="trendParams.type === 'week' ? 'primary' : ''"
              @click="changeTrendType('week', 12)"
            >
              周
            </el-button>
            <el-button
              size="small"
              :type="trendParams.type === 'month' ? 'primary' : ''"
              @click="changeTrendType('month', 12)"
            >
              月
            </el-button>
          </div>
        </div>
        <div ref="trendChart" class="chart-wrapper"></div>
      </div>

      <div class="chart-card">
        <h2>用户活跃度分布</h2>
        <div ref="distributionChart" class="chart-wrapper"></div>
      </div>
    </div>

    <!-- 最近活跃用户列表 -->
    <div class="users-list-container">
      <div class="table-header">
        <h2>最近活跃用户</h2>
        <div class="filter-controls">
          <div class="time-buttons">
            <el-button
              size="small"
              :type="userParams.day === 7 ? 'primary' : ''"
              @click="changeTimeRange(7)"
            >
              周
            </el-button>
            <el-button
              size="small"
              :type="userParams.day === 30 ? 'primary' : ''"
              @click="changeTimeRange(30)"
            >
              月
            </el-button>
            <el-button
              size="small"
              :type="userParams.day === 180 ? 'primary' : ''"
              @click="changeTimeRange(180)"
            >
              半年
            </el-button>
            <el-button
              size="small"
              :type="userParams.day === 365 ? 'primary' : ''"
              @click="changeTimeRange(365)"
            >
              一年
            </el-button>
          </div>
          <el-form :model="userParams" :inline="true" class="search-form">
            <el-form-item label="用户名" style="font-weight: bold">
              <el-input
                v-model="userParams.username"
                placeholder="请输入用户名"
                clearable
                @clear="handleSearch"
                @keyup.enter.native="handleSearch"
              />
            </el-form-item>
            <el-form-item label="真实姓名" style="font-weight: bold">
              <el-input
                v-model="userParams.realName"
                placeholder="请输入真实姓名"
                clearable
                @clear="handleSearch"
                @keyup.enter.native="handleSearch"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch">搜索</el-button>
              <el-button @click="handleReset">重置</el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <el-table
        :data="usersData.records"
        stripe
        v-loading="loading"
        style="width: 100%"
      >
        <el-table-column prop="userId" label="用户ID" width="180" />
        <el-table-column prop="username" label="用户名" width="300" />
        <el-table-column prop="realName" label="真实姓名" width="300" />
        <el-table-column prop="lastActiveText" label="最后活跃时间" width="200" />
        <el-table-column prop="inactiveDays" label="不活跃天数" width="200" align="center">
          <template #default="scope">
            <el-tag :type="getInactiveDaysType(scope.row.inactiveDays)" effect="dark">
              {{ scope.row.inactiveDays }} 天
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="activityLevel" label="活跃等级" width="300" align="center">
          <template #default="scope">
            <el-tag :type="getLevelType(scope.row.activityLevel)" effect="dark">
              {{ scope.row.activityLevel }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="loginCount" label="登录次数" width="300" align="center" />
        <el-table-column label="操作" width="120" align="center">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetail(scope.row)">
              查看详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="userParams.pageNumber"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="userParams.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="usersData.total"
        />
      </div>
    </div>

    <!-- 用户详情弹窗 -->
    <el-dialog
      title="用户活跃详情"
      :visible.sync="detailDialogVisible"
      width="80%"
      :close-on-click-modal="false"
    >
      <div class="user-detail-container">
        <!-- 用户基本信息 -->
        <el-card class="user-info-card" shadow="never">
          <div class="user-basic-info">
            <div class="info-item">
              <label>用户ID：</label>
              <span>{{ currentUser.userId }}</span>
            </div>
            <div class="info-item">
              <label>用户名：</label>
              <span>{{ currentUser.username }}</span>
            </div>
            <div class="info-item">
              <label>真实姓名：</label>
              <span>{{ currentUser.realName }}</span>
            </div>
          </div>
        </el-card>

        <!-- 活跃记录表格 -->
        <el-card class="activity-table-card" shadow="never">
          <template #header>
            <div class="card-header">
              <span>活跃记录</span>
              <el-button type="primary" size="small" @click="loadUserActivityData">
                刷新
              </el-button>
            </div>
          </template>

          <el-table
            :data="userActivityData.records"
            v-loading="activityLoading"
            stripe
            style="width: 100%"
          >
            <el-table-column prop="activeDate" label="活跃日期" width="120" align="center" />
            <el-table-column prop="activeTime" label="活跃时间" width="160" align="center" />
            <el-table-column prop="lastActiveTime" label="时间戳" width="160" align="center" />
            <el-table-column prop="activeDate" label="活跃日期" width="140" align="center" />
            <el-table-column prop="loginCount" label="登录次数" width="100" align="center" />
          </el-table>

          <!-- 分页 -->
          <div class="pagination-container">
            <el-pagination
              @size-change="handleActivitySizeChange"
              @current-change="handleActivityCurrentChange"
              :current-page="activityParams.pageNumber"
              :page-sizes="[10, 20, 50]"
              :page-size="activityParams.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="userActivityData.total"
            />
          </div>
        </el-card>
      </div>

      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import 'echarts/theme/macarons'
import {
  activeAnalyse,
  activeTrend,
  activeRecent,
  activeRecentInfo
} from '@/api/auth/index'

export default {
  name: 'UserActivityAnalysis',
  data() {
    return {
      // 统计数据
      statsData: {
        totalUsers: 0,
        totalUsersChangePercent: 0,
        dau: 0,
        dauChangePercent: 0,
        wau: 0,
        wauChangePercent: 0,
        mau: 0,
        mauChangePercent: 0,
        highActiveUsers: 0,
        mediumActiveUsers: 0,
        lowActiveUsers: 0,
        dormantUsers: 0
      },
      // 趋势图数据
      trendData: {
        dates: [],
        newUsers: [],
        activeUsers: []
      },
      // 用户列表数据
      usersData: {
        records: [],
        total: 0
      },
      // 图表实例
      trendChart: null,
      distributionChart: null,
      // 加载状态
      loading: false,
      // 趋势图参数
      trendParams: {
        type: 'day',
        limit: 30
      },
      // 用户列表参数
      userParams: {
        pageNumber: 1,
        pageSize: 10,
        username: '',
        realName: '',
        day: 7 // 默认显示最近一周的数据
      },
      // 用户详情弹窗相关数据
      detailDialogVisible: false,
      currentUser: {
        userId: '',
        username: '',
        realName: ''
      },
      userActivityData: {
        records: [],
        total: 0
      },
      activityLoading: false,
      activityParams: {
        pageNumber: 1,
        pageSize: 10,
        userId: ''
      }
    }
  },
  mounted() {
    // 加载数据
    this.loadData()
    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    // 组件销毁时清除图表实例
    if (this.trendChart) {
      this.trendChart.dispose()
    }
    if (this.distributionChart) {
      this.distributionChart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 返回首页
    goBack() {
      this.$router.push('/')
    },

    // 加载数据
    loadData() {
      this.loadStatsData()
      this.loadTrendData()
      this.loadUsersData()
    },

    // 加载统计数据
    async loadStatsData() {
      try {
        const response = await activeAnalyse()
        this.statsData = response.data
        // 数据加载后初始化分布图表
        this.$nextTick(() => {
          this.initDistributionChart()
        })
      } catch (error) {
        console.error('加载统计数据失败:', error)
        this.$message.error('加载统计数据失败')
      }
    },

    // 加载趋势数据
    async loadTrendData() {
      try {
        const response = await activeTrend(this.trendParams)
        this.trendData = response.data
        // 数据加载后初始化趋势图表
        this.$nextTick(() => {
          this.initTrendChart()
        })
      } catch (error) {
        console.error('加载趋势数据失败:', error)
        this.$message.error('加载趋势数据失败')
      }
    },

    // 加载用户数据
    async loadUsersData() {
      this.loading = true
      try {
        const response = await activeRecent(this.userParams)
        this.usersData = response.data
      } catch (error) {
        console.error('加载用户数据失败:', error)
        this.$message.error('加载用户数据失败')
        this.usersData = {
          records: [],
          total: 0
        }
      } finally {
        this.loading = false
      }
    },

    // 改变时间范围
    changeTimeRange(days) {
      this.userParams.day = days
      this.userParams.pageNumber = 1 // 重置到第一页
      this.loadUsersData()
    },

    // 改变趋势图类型
    changeTrendType(type, limit) {
      this.trendParams.type = type
      this.trendParams.limit = limit
      this.loadTrendData()
    },

    // 搜索用户
    handleSearch() {
      this.userParams.pageNumber = 1
      this.loadUsersData()
    },

    // 重置搜索条件
    handleReset() {
      this.userParams.username = ''
      this.userParams.realName = ''
      this.userParams.pageNumber = 1
      this.loadUsersData()
    },

    // 改变每页显示数量
    handleSizeChange(pageSize) {
      this.userParams.pageSize = pageSize
      this.userParams.pageNumber = 1
      this.loadUsersData()
    },

    // 改变当前页码
    handleCurrentChange(pageNumber) {
      this.userParams.pageNumber = pageNumber
      this.loadUsersData()
    },

    // 查看用户详情
    handleViewDetail(user) {
      this.currentUser = {
        userId: user.userId,
        username: user.username,
        realName: user.realName
      }
      this.activityParams.userId = user.userId
      this.activityParams.pageNumber = 1

      this.detailDialogVisible = true
      this.loadUserActivityData()
    },

    // 加载用户活跃数据
    async loadUserActivityData() {
      this.activityLoading = true
      try {
        const response = await activeRecentInfo(this.activityParams)
        this.userActivityData = response.data
      } catch (error) {
        console.error('加载用户活跃数据失败:', error)
        this.$message.error('加载用户活跃数据失败')
        this.userActivityData = {
          records: [],
          total: 0
        }
      } finally {
        this.activityLoading = false
      }
    },

    // 改变活跃数据每页显示数量
    handleActivitySizeChange(pageSize) {
      this.activityParams.pageSize = pageSize
      this.activityParams.pageNumber = 1
      this.loadUserActivityData()
    },

    // 改变活跃数据当前页码
    handleActivityCurrentChange(pageNumber) {
      this.activityParams.pageNumber = pageNumber
      this.loadUserActivityData()
    },



    // 根据不活跃天数获取标签类型
    getInactiveDaysType(days) {
      if (days <= 3) return 'success'
      if (days <= 7) return 'warning'
      return 'danger'
    },

    // 根据活跃等级获取标签类型
    getLevelType(level) {
      const levelMap = {
        '高活跃': 'success',
        '中活跃': 'warning',
        '低活跃': 'info',
        '沉睡用户': 'danger'
      }
      return levelMap[level] || 'info'
    },

    // 初始化活跃度趋势折线图
    initTrendChart() {
      if (!this.$refs.trendChart) return

      this.trendChart = echarts.init(this.$refs.trendChart, 'macarons')

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['新增用户', '活跃用户']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.trendData.dates || [],
          axisLabel: {
            rotate: 45
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '新增用户',
            type: 'line',
            data: this.trendData.newUsers || [],
            smooth: true,
            lineStyle: {
              width: 3
            },
            itemStyle: {
              color: '#3498db'
            },
            symbol: 'circle',
            symbolSize: 6
          },
          {
            name: '活跃用户',
            type: 'line',
            data: this.trendData.activeUsers || [],
            smooth: true,
            lineStyle: {
              width: 3
            },
            itemStyle: {
              color: '#2ecc71'
            },
            symbol: 'circle',
            symbolSize: 6
          }
        ]
      }

      this.trendChart.setOption(option)
    },

    // 初始化用户活跃度分布饼状图
    initDistributionChart() {
      if (!this.$refs.distributionChart) return

      this.distributionChart = echarts.init(this.$refs.distributionChart, 'macarons')

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 10,
          top: 'center'
        },
        series: [
          {
            name: '用户活跃度分布',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 18,
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: [
              {
                value: this.statsData.highActiveUsers || 0,
                name: '高活跃用户'
              },
              {
                value: this.statsData.mediumActiveUsers || 0,
                name: '中活跃用户'
              },
              {
                value: this.statsData.lowActiveUsers || 0,
                name: '低活跃用户'
              },
              {
                value: this.statsData.dormantUsers || 0,
                name: '沉睡用户'
              }
            ]
          }
        ],
        color: ['#2ecc71', '#3498db', '#f39c12', '#e74c3c']
      }

      this.distributionChart.setOption(option)
    },

    // 窗口大小变化处理
    handleResize() {
      if (this.trendChart) {
        this.trendChart.resize()
      }
      if (this.distributionChart) {
        this.distributionChart.resize()
      }
    },

    // 格式化数字显示
    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    // 获取变化百分比样式类
    getChangeClass(percent) {
      if (percent > 0) return 'positive'
      if (percent < 0) return 'negative'
      return ''
    }
  }
}
</script>

<style scoped>
.analysis-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e8e8e8;
}

.page-header h1 {
  color: #2c3e50;
  margin: 0;
  font-size: 24px;
}

.back-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background: #2980b9;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s, box-shadow 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.stat-card.primary {
  border-top: 4px solid #3498db;
}

.stat-card.success {
  border-top: 4px solid #2ecc71;
}

.stat-card.warning {
  border-top: 4px solid #f39c12;
}

.stat-card.danger {
  border-top: 4px solid #e74c3c;
}

.stat-title {
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #2c3e50;
}

.stat-change {
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.positive {
  color: #2ecc71;
}

.negative {
  color: #e74c3c;
}

.charts-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.chart-card h2 {
  color: #2c3e50;
  margin: 0;
  font-size: 18px;
}

.date-buttons {
  display: flex;
  gap: 8px;
}

.chart-wrapper {
  width: 100%;
  height: 400px;
}

.users-list-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: inline-flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.filter-controls {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 15px;
}

.time-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.users-list-container h2 {
  color: #2c3e50;
  margin: 0;
  font-size: 18px;
}

.search-form {
  margin-bottom: 0;
  margin-left: 50px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 用户详情弹窗样式 */
.user-detail-container {
  max-height: 70vh;
  overflow-y: auto;
}

.user-info-card {
  margin-bottom: 20px;
  border: 1px solid #e6e6e6;
}

.user-basic-info {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  padding: 15px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-item label {
  font-weight: bold;
  color: #606266;
  margin-right: 8px;
  min-width: 80px;
}

.info-item span {
  color: #303133;
  font-weight: 500;
}

.activity-table-card {
  margin-top: 20px;
  border: 1px solid #e6e6e6;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e6e6e6;
}

.card-header span {
  font-weight: 600;
  color: #2c3e50;
}

/* Element UI 样式覆盖 */
:deep(.el-table) {
  margin-top: 10px;
}

:deep(.el-form--inline .el-form-item) {
  margin-bottom: 0;
  margin-right: 10px;
}

:deep(.el-tag) {
  border: none;
  font-weight: 500;
}

:deep(.el-dialog__body) {
  padding: 20px;
}

:deep(.el-card__header) {
  padding: 0;
  border-bottom: none;
}

:deep(.el-table th) {
  background-color: #f8f9fa;
  color: #2c3e50;
  font-weight: 600;
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
  background-color: #fafafa;
}

:deep(.el-table__body tr:hover > td) {
  background-color: #f5f7fa !important;
}

@media (max-width: 1200px) {
  .charts-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .time-buttons {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .stats-container {
    grid-template-columns: 1fr 1fr;
  }

  .analysis-page {
    padding: 15px;
  }

  .user-basic-info {
    flex-direction: column;
    gap: 15px;
  }

  .info-item {
    width: 100%;
  }

  :deep(.el-table .cell) {
    padding: 8px 4px;
    font-size: 12px;
  }
}

@media (max-width: 576px) {
  .stats-container {
    grid-template-columns: 1fr;
  }

  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .search-form {
    flex-direction: column;
    width: 100%;
  }

  :deep(.el-form--inline .el-form-item) {
    margin-right: 0;
    margin-bottom: 10px;
    width: 100%;
  }

  .time-buttons {
    justify-content: space-between;
  }

  .time-buttons .el-button {
    flex: 1;
    min-width: 60px;
    margin: 2px;
  }

  .chart-wrapper {
    height: 300px;
  }
}

@media (max-width: 480px) {
  .analysis-page {
    padding: 10px;
  }

  .stat-card {
    padding: 15px;
  }

  .stat-value {
    font-size: 24px;
  }

  .users-list-container {
    padding: 15px;
  }

  :deep(.el-pagination) {
    font-size: 12px;
  }
}
</style>
